import { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import CryptoJS from "crypto-js";
import { API_HOST, SECRET_KEY } from "../constants";
import type { Task, TelegramUser } from "../types";

// Кеш для задач по ID
const taskCache: Record<string, { task: Task; timestamp: number }> = {};
const taskCachePromises: Record<string, Promise<Task>> = {};
const TASK_CACHE_DURATION = 5 * 60 * 1000; // 5 минут

export const useTaskLoader = (user: TelegramUser | null) => {
  const { id } = useParams();
  const location = useLocation();
  const [task, setTask] = useState<Task | null>(null);
  const [sql, setSql] = useState("");
  const [loading, setLoading] = useState(true);
  const sqlInitializedRef = useRef(false);
  const taskIdRef = useRef<string | undefined>(id);
  const isMountedRef = useRef(true);

  // Функция инициализации SQL
  const initializeSql = (taskData: Task, search: string, currentUser: TelegramUser | null) => {
    if (sqlInitializedRef.current) return;

    const params = new URLSearchParams(search);
    const query = params.get("query");
    
    // 1. Приоритет: SQL из URL
    if (query) {
      try {
        const bytes = CryptoJS.AES.decrypt(decodeURIComponent(query), SECRET_KEY);
        const originalSql = bytes.toString(CryptoJS.enc.Utf8);
        if (originalSql) {
          setSql(originalSql);
          sqlInitializedRef.current = true;
          return;
        }
      } catch {
        console.warn("Не удалось расшифровать запрос");
      }
    }
    
    // 2. Если нет SQL в URL и есть пользователь - загружаем последний код
    if (!sqlInitializedRef.current && currentUser) {
      const fetchLastCode = async () => {
        try {
          const res = await fetch(`${API_HOST}/user/${currentUser.id}/task/${taskData.id}/code`);
          if (res.ok) {
            const data = await res.json();
            if (data?.code) {
              setSql(data.code);
              sqlInitializedRef.current = true;
              return;
            }
          }
        } catch (err) {
          console.error("Не удалось загрузить последний код:", err);
        }
        
        // 3. Fallback: boilerplate
        if (!sqlInitializedRef.current && taskData.boilerplate) {
          setSql(taskData.boilerplate);
          sqlInitializedRef.current = true;
        }
      };
      fetchLastCode();
    } else if (!sqlInitializedRef.current && taskData.boilerplate) {
      // 3. Fallback: boilerplate (если нет пользователя)
      setSql(taskData.boilerplate);
      sqlInitializedRef.current = true;
    }
  };

  // Загрузка задачи - только при изменении ID
  useEffect(() => {
    isMountedRef.current = true;

    const fetchTask = async () => {
      if (!id) {
        if (isMountedRef.current) {
          setTask(null);
          setLoading(false);
        }
        return;
      }
      
      // Сбрасываем флаги при смене задачи
      if (taskIdRef.current !== id) {
        sqlInitializedRef.current = false;
        taskIdRef.current = id;
      }

      // Проверяем кеш
      const cached = taskCache[id];
      const now = Date.now();
      if (cached && (now - cached.timestamp) < TASK_CACHE_DURATION) {
        if (isMountedRef.current) {
          setTask(cached.task);
          setLoading(false);
        }
        return;
      }

      // Если уже есть запрос для этого ID, ждем его
      if (taskCachePromises[id]) {
        try {
          const cachedTask = await taskCachePromises[id];
          if (isMountedRef.current) {
            setTask(cachedTask);
            setLoading(false);
          }
          return;
        } catch {
          // Продолжаем с новым запросом
          delete taskCachePromises[id];
        }
      }
      
      if (isMountedRef.current) {
        setLoading(true);
      }
      
      // Создаем промис для загрузки задачи
      const fetchPromise = (async () => {
        try {
          const res = await fetch(`${API_HOST}/api/tasks/${id}`);
          if (!res.ok) throw new Error("Не удалось загрузить задачу");
          
          const json = await res.json();
          const taskData = json.data?.[0];
          if (!taskData) throw new Error("Задача не найдена");
          
          // Обновляем кеш
          taskCache[id] = { task: taskData, timestamp: now };
          delete taskCachePromises[id];
          
          return taskData;
        } catch (err) {
          delete taskCachePromises[id];
          throw err;
        }
      })();

      // Сохраняем промис для предотвращения дублирующихся запросов
      taskCachePromises[id] = fetchPromise;
      
      try {
        const taskData = await fetchPromise;
        
        if (!isMountedRef.current) return;
        
        setTask(taskData);
      } catch (err) {
        if (isMountedRef.current) {
          console.error(err);
          setTask(null);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchTask();

    return () => {
      isMountedRef.current = false;
    };
  }, [id]); // Только ID - задача не зависит от user или location.search

  // Инициализация SQL - отдельный эффект, который зависит от task, location.search и user
  useEffect(() => {
    if (!task || sqlInitializedRef.current) return;
    
    initializeSql(task, location.search, user);
  }, [task, location.search, user]);

  return { task, sql, setSql, loading };
};
